import { Button, Row, Space, Spin, Typography, UploadFile, message as antMessage } from "antd";
import { RcFile } from "antd/es/upload";
import dayjs from "dayjs";
import isEmpty from "lodash/isEmpty";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import StatusBlackIcon from "../../assets/icons/status-circle-black.svg";
import { ACCEPTED, CREATOR, FILE, MAX_MEDIAS_TO_SEND, MESSAGE_TIME_FORMAT, PENDING, REJECTED, THUMBNAIL_TYPES } from "../../helpers/constant";
import { IMessage } from "../../helpers/types";
import { setPendingMediaFiles } from "../../redux/mediaPush/mediaPushSlice";
import { StoreState } from "../../redux/store";
import { mediapushService, storageService } from "../../services";
import { socket } from "../../socket";
import { generateURL } from "../../utls/FunctionsUtil";
import ConversationMediaComponent from "../AddMediaComponent/ConversationMediaComponent";
import IdentificationComponent from "../IdentificationComponent/IdentificationComponent";
import MediaComponent from "../MediaComponent";
import MessageDeleteMenu from "../MessageDeleteMenu/MessageDeleteMenu";
import MessagePriceComponent from "../MessagePriceComponent";
import SpinnerComponent from "../SpinnerComponent/SpinnerComponent";
import MessageReadStatusIndicator from "../MessageReadStatusIndicator/MessageReadStatusIndicator";
import "./index.less";

// Helper function to format text with bold @ mentions and replace /name with username
const formatTextWithMentions = (text: string, navigate: (path: string) => void, interlocutorUsername?: string): React.ReactNode[] => {
  // First replace /name with the interlocutor's username
  let processedText = text;
  if (interlocutorUsername) {
    processedText = text.replace(/\/name/g, interlocutorUsername);
  }

  // Then split by @ mentions and apply bold formatting with navigation
  const parts = processedText.split(/(@\w+)/g);
  return parts.map((part, index) => {
    if (part.startsWith("@")) {
      const username = part.substring(1); // Remove the @ symbol
      return (
        <strong key={index} onClick={() => navigate(`/p/${username}`)} style={{ cursor: "pointer", color: "#ffffff" }}>
          {part}
        </strong>
      );
    }
    return <React.Fragment key={index}>{part}</React.Fragment>;
  });
};

type props = {
  message: IMessage;
  expired?: boolean;
  hideStatus?: boolean;
  isBlur?: boolean;
  displayVideo?: boolean;
  income?: boolean; // Add income prop to indicate if message is from creator
  identifications?: string[]; // Array of user IDs for identification component
  canDelete?: boolean;
  isDeleting?: boolean;
  onDeleteMessage?: (messageId: string) => void;
};

const MessageMediaItemComponent: React.FC<props> = ({
  message,
  expired,
  hideStatus,
  isBlur = true,
  displayVideo = false,
  income = false,
  canDelete = false,
  isDeleting = false,
  onDeleteMessage,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [files, setFiles] = useState<any>([]);
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState<number>(0);
  const [loadedData, setLoadedData] = useState<number>(0);
  const [totalSize, setTotalSize] = useState<number>(0);
  const { pendingMediaFiles } = useSelector((state: StoreState) => state.mediaPushes);
  const { selectedConversation } = useSelector((state: StoreState) => state.messages);
  const dispatch = useDispatch();
  const messageTime = dayjs(message.createdAt);

  const handleCopy = async () => {
    const text = message.text || "";
    if (!text) return;

    if (navigator?.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      antMessage.success(t("message-copied"));
      return;
    }

    const textarea = document.createElement("textarea");
    textarea.value = text;
    textarea.setAttribute("readonly", "");
    textarea.style.position = "absolute";
    textarea.style.left = "-9999px";
    document.body.appendChild(textarea);
    textarea.select();
    document.execCommand("copy");
    document.body.removeChild(textarea);
    antMessage.success(t("message-copied"));
  };

  const interlocutorUsername = selectedConversation?.interlocutor?.firstName;

  useEffect(() => {
    function onProgressUpdate(data: number) {
      setProgress((prev) => {
        const prog = prev + 1;
        if (prog <= 99) {
          return prog;
        }
        return prev;
      });
    }

    function onProcessEnd() {
      setLoading(false);
    }

    socket.on("file-updated", onProcessEnd);
    socket.on("progress-update", onProgressUpdate);

    return () => {
      socket.off("file-updated", onProcessEnd);
      socket.off("progress-update", onProgressUpdate);
    };
  }, []);

  useEffect(() => {
    if (totalSize > 0) {
      const uploadProgress = Math.round((loadedData / totalSize) * 100);
      const globalProgress = Math.round(uploadProgress);
      setProgress((prev) => {
        if (globalProgress > prev) {
          return globalProgress;
        }
        return prev;
      });
    }
  }, [loadedData, totalSize]);

  const handleFilesChange = async (fileList: UploadFile<[]>[]) => {
    // Merge the files and filter duplicates (assuming unique by 'uid' or 'name')
    const uniqueFiles = [...pendingMediaFiles, ...fileList].filter((value, index, self) => self.findIndex((v) => v.uid === value.uid) === index);

    // Process files asynchronously
    const updatedFiles = await Promise.all(
      uniqueFiles.map(async (item) => {
        if (item.imgType !== "quick-file") {
          const src = await generateURL(item.originFileObj as RcFile);
          return { ...item, thumbUrl: src };
        }
        return item;
      })
    );

    // Update state and dispatch actions
    dispatch(setPendingMediaFiles({ files: updatedFiles }));
    setFiles(updatedFiles);
  };

  const handleSendMedias = async () => {
    if (!isEmpty(files) && !loading) {
      setLoading(true);
      const formData = new FormData();
      formData.set("messageId", message._id!);
      formData.set("mediaPushId", message.mediaPushId!);
      const videoFileToChunk: any[] = [];
      const quickFiles: any = [];

      files.forEach((file: any) => {
        if (file?.imgType === "quick-file") {
          quickFiles.push({ _id: file._id });
        } else {
          if (file?.originFileObj?.type?.includes("image/")) {
            formData.append(`files`, file.originFileObj as any);
          } else {
            videoFileToChunk.push(file);
          }
        }
      });
      formData.append("quickFiles", JSON.stringify(quickFiles));

      if (videoFileToChunk.length > 0) {
        const videoFiles: any[] = [];
        const totalSizes = videoFileToChunk.reduce((acc, file) => acc + file.size, 0);
        setTotalSize(totalSizes);

        for (let i = 0; i < videoFileToChunk.length; i++) {
          const file = videoFileToChunk[i];
          const result = await storageService.uploadFileMultiPart(file, setLoadedData);
          videoFiles.push({
            Location: result.Location,
            isPrivate: file.isPrivate,
            Key: result.Key,
            ETag: result.ETag,
            Bucket: result.Bucket,
            filename: file.name,
            size: file.size,
          });
        }

        formData.append("videoFiles", JSON.stringify(videoFiles));
      }
      await mediapushService.updateMediaPushFiles(formData).then(() => {
        setLoading(false);
        dispatch(setPendingMediaFiles({ files: [] }));
      });
    }
  };

  const hasIdentification = Array.isArray(message.taggedCreators) && message.taggedCreators.length > 0;

  const messageContent = (
    <Space
      className={`messageMediaItem ${expired ? "expired" : ""} ${message.status === ACCEPTED ? "accepted" : ""} ${income ? "income" : ""} ${hasIdentification ? "identification" : ""}`}
      direction="vertical"
    >
      {message?._id && onDeleteMessage && (
        <div className="message-top-action">
          <MessageDeleteMenu
            canDelete={canDelete}
            isDeleting={isDeleting}
            onDelete={() => onDeleteMessage(message._id!)}
            showCopy={true}
            copyDisabled={!Boolean(message.text?.trim())}
            onCopy={handleCopy}
          />
        </div>
      )}

      <Spin spinning={loading} style={{ maxHeight: "100%" }} indicator={<SpinnerComponent progress={true} progressValue={progress} />}>
        <IdentificationComponent identifications={message.taggedCreators || []} role={CREATOR} />
        <Row>
          <Space className="w-full gap-4" direction="vertical">
            <MessagePriceComponent message={message} income={income} />

            {message.medias && !isEmpty(message.medias) ? (
              <Row>
                {message.medias?.map((media) => {
                  let imageUrl = null;
                  const isVideo = media?.url?.includes(".mp4");
                  const isAudio =
                    media?.isAudio ||
                    media?.url?.includes(".mp3") ||
                    media?.url?.includes(".wav") ||
                    media?.url?.includes(".m4a") ||
                    media?.url?.includes(".ogg") ||
                    media?.url?.includes(".aac") ||
                    media?.url?.includes(".webm") ||
                    media?.url?.includes("audio/");
                  if (isBlur) {
                    imageUrl = media.thumbnails?.find((thumb: any) => thumb.type === THUMBNAIL_TYPES.BLURRED)?.absolutePath || media.url;
                  } else {
                    imageUrl = media.thumbnails?.find((thumb: any) => thumb.type === THUMBNAIL_TYPES.CLEAR)?.absolutePath || media.url;
                  }
                  return (
                    <MediaComponent
                      key={media.fileId}
                      width={60}
                      height={60}
                      borderRadius={11}
                      image={imageUrl}
                      url={media.url}
                      isVideo={isVideo}
                      isAudio={isAudio}
                      username={""}
                      isPrivate={media.isPrivate}
                      isBlur={isBlur}
                      shouldAddWatermark={false}
                      displayVideo={displayVideo}
                    />
                  );
                })}
              </Row>
            ) : message.uploading ? (
              <Row className="mt-5 justify-center">
                <Spin size="large" />
              </Row>
            ) : (
              !expired &&
              message.status !== REJECTED && (
                <Row className="mt-5">
                  <ConversationMediaComponent
                    disabled={message.status === PENDING}
                    onFilesChange={handleFilesChange}
                    showTitle={false}
                    maxCount={MAX_MEDIAS_TO_SEND}
                    messageId={message._id}
                  />
                </Row>
              )
            )}
          </Space>
        </Row>
        {message.text && (
          <Row className="mt-7">
            <Typography.Text className="font-15-medium text-black-color">
              {message.taggedCreators && Array.isArray(message.taggedCreators) && message.taggedCreators.length > 0 && message.text.includes("@")
                ? formatTextWithMentions(message.text, navigate, interlocutorUsername)
                : message.text.includes("/name") && interlocutorUsername
                  ? message.text.replace(/\/name/g, interlocutorUsername)
                  : message.text}
            </Typography.Text>
          </Row>
        )}
        {isEmpty(message.medias) && !expired && message.status !== REJECTED && !message.uploading && (
          <Button
            className="sendMediaButton"
            onClick={handleSendMedias}
            disabled={isEmpty(files) || (message.status !== REJECTED && message.status === PENDING)}
          >
            {t("send")}
          </Button>
        )}
        <Row className="mt-10">
          <Space style={{ width: "100%", justifyContent: "space-between" }}>
            <div className="message-media-left-actions">
              {!hideStatus ? (
                <div>
                  <img src={StatusBlackIcon} alt={message.status} />
                  <Typography.Text className="font-14-bold text-black-color pl-4">
                    {t(message.status!)} {isEmpty(message.medias) && message.status === ACCEPTED && message.type === FILE ? `(${t(PENDING)})` : ""}
                  </Typography.Text>
                </div>
              ) : (
                <div></div>
              )}
            </div>

            <div
              style={{
                minWidth: "60px",
                height: "20px",
                marginBottom: "3px",
                display: "flex",
                alignItems: "flex-end",
                justifyContent: "flex-end",
                gap: "2px",
                flexShrink: 0,
              }}
            >
              {
                <Typography.Text className={`message-time`} style={{ paddingLeft: "8px", marginBottom: "1px", whiteSpace: "nowrap" }}>
                  {messageTime.format(MESSAGE_TIME_FORMAT)}
                </Typography.Text>
              }
              <MessageReadStatusIndicator isRead={message.isRead || false} />
            </div>
          </Space>
        </Row>
      </Spin>
    </Space>
  );

  return (
    <>
      {message?._id && onDeleteMessage ? (
        <MessageDeleteMenu
          canDelete={canDelete}
          isDeleting={isDeleting}
          onDelete={() => onDeleteMessage(message._id!)}
          showCopy={true}
          copyDisabled={!Boolean(message.text?.trim())}
          onCopy={handleCopy}
          enableLongPress={true}
        >
          {messageContent}
        </MessageDeleteMenu>
      ) : (
        messageContent
      )}
    </>
  );
};

export default MessageMediaItemComponent;
